<template>
  <div class="booking-services-root">
    <v-row v-if="isLoadingServices">
      <v-col cols="12" class="ml-2 mr-4">
        <v-skeleton-loader class="mt-4" :type="loadingAnimation"></v-skeleton-loader>
      </v-col>
    </v-row>
    <div v-else-if="bookableServices.length">
      <v-card-text class="title font-weight-bold">{{ $t("bookingForm.BookingServices.title") }}</v-card-text>
      <!-- New design for desktop -->
      <v-row v-if="showListView" no-gutters>
        <v-col cols="4" v-for="item in bookableServices" :key="item.serviceId">
          <booking-services-item :key="item.serviceId" :item="item" @click="selectService(item)" />
        </v-col>
      </v-row>
      <!-- Fallback layout for services (old design + mobile): uses a virtual scroll list in iframe mode and a standard row layout otherwise -->
      <template v-else>
        <v-virtual-scroll
          v-if="bookingIframe"
          class="overflow-y-auto mb-4 booking-services-scroll"
          :height="height"
          item-height="75"
          :items="bookableServices"
        >
          <template v-slot:default="{ item }">
            <booking-services-item :item="item" :small="false" @click="selectService(item)" />
          </template>
        </v-virtual-scroll>
        <v-row v-else no-gutters>
          <v-col cols="12" v-for="item in bookableServices" :key="item.serviceId">
            <booking-services-item :item="item" :small="false" @click="selectService(item)" />
          </v-col>
        </v-row>
      </template>
    </div>

    <v-row v-else class="fill-height mt-10 mb-12" :style="`height: ${height}`" align="stretch">
      <v-col cols="12"><v-img class="mt-10" contain :src="image" :height="150" justify="center" /></v-col>
      <v-col class="justify-center align-center text-center" cols="12">
        <v-card-text>
          {{ $t("bookingForm.BookingServices.noServices", { partnerMail: partnerMail }) }}
        </v-card-text>
      </v-col>
    </v-row>
    <booking-navigation :isMarginTop="true" @back="back" @forward="forward" :isMobile="isMobile"></booking-navigation>
  </div>
</template>

<script lang="ts">
import { AssetEnum, AssetRepository } from "@/lib/AssetRepository";
import { BookingServiceBookingViewModelGen } from "@/services/booking/v1/data-contracts";
import { AvailabilityModule } from "@/store/modules/availability.store";
import { PartnerModule } from "@/store/modules/partner";
import { Component, Prop, Vue } from "vue-property-decorator";
import BookingNavigation from "./BookingNavigation.vue";
import BookingSelectedService from "./BookingSelectedService.vue";
import BookingServicesItem from "./BookingServicesItem.vue";

@Component({ components: { BookingNavigation, BookingSelectedService, BookingServicesItem } })
export default class BookingServices extends Vue {
  @Prop({ default: false })
  isMobile!: boolean;

  /**
   * Flag if booking is used in an iframe(design should not change)
   */
  @Prop({ default: false })
  bookingIframe!: boolean;

  get showListView() {
    if (this.bookingIframe) {
      return false;
    }

    if (this.isMobile) {
      return false;
    }

    return true;
  }

  get image() {
    return AssetRepository.getAsset(this.$vuetify.theme.dark, AssetEnum.outOfService);
  }

  get partner() {
    return PartnerModule.partner;
  }

  get partnerMail() {
    return this.partner?.contact?.email || "";
  }

  back() {
    AvailabilityModule.setStart();
  }

  forward() {
    AvailabilityModule.setCalender();
  }

  get loadingAnimation() {
    if (this.isMobile) {
      return "heading, list-item-avatar-three-line, list-item-avatar-three-line, list-item-avatar-three-line, list-item-avatar-three-line, list-item-avatar-three-line";
    }
    return "heading, list-item-avatar-three-line, list-item-avatar-three-line, list-item-avatar-three-line, list-item-avatar-three-line, list-item-avatar-three-line, list-item-avatar-three-line";
  }

  get height() {
    return "495";
  }

  get bookableServices(): BookingServiceBookingViewModelGen[] {
    return AvailabilityModule.paginationList;
  }

  get isLoadingServices() {
    return AvailabilityModule.isLoadingServices;
  }

  get serviceId() {
    return AvailabilityModule.serviceId;
  }

  set serviceId(id: string) {
    AvailabilityModule.setServiceId(id);
  }

  selectService(item: BookingServiceBookingViewModelGen) {
    this.$log.debug(item);
    this.serviceId = item.serviceId;
    AvailabilityModule.setCalender();
  }
}
</script>
<style scoped>
::-webkit-scrollbar {
  -webkit-appearance: none;
  width: 7px;
}

::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background-color: rgba(0, 0, 0, 0.5);
}
.overflow-y-auto {
  overflow: scroll;
}
.booking-services-scroll {
  padding-bottom: 70px;
  margin-bottom: 70px;
}
.booking-services-root {
  padding-bottom: 90px;
}
</style>
